import { IoSearchOutline } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import React, { useContext, useEffect, useState } from 'react';
import Button from '../../components/shared/custom-btn';
import Heading from '../../components/shared/heading';
import VendorsTable from './vendor-table';
import Modal from '../../components/shared/modal';
import AddModal from './add-modal';
import { AccountsContext } from '../../components/context/account-context';
import { getAllVendors, getAllVendorStatuses } from '../../services/vendors-service';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from '../../assets/images/logo.jpg';
import Help from './help';
import { usePageHelp } from '../../components/context/help-context';


const Vendors = () => {
    usePageHelp(Help);
    const { accountId } = useContext(AccountsContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [openAddModal, setOpenAddModal] = useState(false);
    const [newlyAddedItem, setNewlyAddedItem] = useState(null);
    const [vendorData, setVendorData] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusOptions, setStatusOptions] = useState([]);
    const [selectedRows, setSelectedRows] = useState({});

    useEffect(() => {
        const fetchGlobalData = async () => {
            if (!accountId) return;
            setLoading(true);

            try {
                const [statusOptionsRes] = await Promise.all([
                    getAllVendorStatuses(accountId),
                ]);

                if (statusOptionsRes.success) setStatusOptions(statusOptionsRes.data.data || []);
            } catch (err) {
                console.error("Failed to fetch global data", err);
                setError('Failed to fetch global data');
            } finally {
                setLoading(false);
            }
        };

        fetchGlobalData();
    }, [accountId]);

    const fetchVendors = async () => {
        setLoading(true);
        try {
            const response = await getAllVendors(accountId);
            console.log(response)
            if (response.success) {
                const sortedData = response.data.data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)); // sort latest first
                setVendorData(sortedData || []);
                setError(null);
            } else {
                setError(response.data.message || 'Failed to fetch vendors records');
            }
        } catch (err) {
            setError('An error occurred while fetching vendor records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVendors();
    }, [accountId]);

    const handleOpenAddModal = () => {
        setOpenAddModal(true);
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
    };

    const handleAddItem = (newItem) => {
        setNewlyAddedItem(newItem);
        fetchVendors();
    };

    const handleOpenEditModal = (item) => {
        setNewlyAddedItem(item);
    };

    const handleClearNewlyAddedItem = () => {
        setNewlyAddedItem(null);
    };

    // Filter data for export based on selected rows
    const exportData = Object.keys(selectedRows).length > 0
        ? vendorData.filter((_, index) => selectedRows[index])
        : vendorData;

    const csvData = exportData.map(item => ({
        'Vendor ID': item.vendorId,
        Name: item.name,
        Location: item.location ? `${parseFloat(item.location.split(',')[0]).toFixed(3)}, ${parseFloat(item.location.split(',')[1]).toFixed(3)}` : 'N/A',
        Address: item.address,
        City: item.city,
        State: item.state,
        Country: item.country,
        'Postal Code': item.postalCode,
        Status: item.status,
    }));

    const handleExportPDF = () => {
        const doc = new jsPDF();
        const logoWidth = 40;
        const logoHeight = 30;
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 2;
        doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);
        autoTable(doc, {
            head: [['Vendor ID', 'Name', 'Location', 'Address', 'City', 'State', 'Country', 'Postal Code', 'Status']],
            body: exportData.map(item => [
                item.vendorId,
                item.name,
                item.location
                    ? `${parseFloat(item.location.split(',')[0]).toFixed(3)}, ${parseFloat(item.location.split(',')[1]).toFixed(3)}`
                    : 'N/A',
                item.address || '',
                item.city || '',
                item.state || '',
                item.country || '',
                item.postalCode || '',
                item.status || '',
            ]),
            startY: logoY + logoHeight,
        });
        doc.save('vendors_list.pdf');
    };

    const isExportDisabled = Object.keys(selectedRows).length === 0;

    return (
        <div className="px-6 py-4">
            <div className='flex mb-2 items-center justify-between'>
                <div>
                    <Heading>Vendors</Heading>
                </div>
                <Button
                    onClick={handleOpenAddModal}
                    label='Add Vendor'
                    className='text-white px-4 !py-1 rounded-lg bg-btnBg'
                />
            </div>

            <div className='space-y-4'>
                {error && (
                    <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-3 flex-wrap">
                        <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
                            <IoSearchOutline className="text-gray-500 text-lg" />
                            <input
                                type="text"
                                className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 items-center">
                        <Button
                            onClick={handleExportPDF}
                            className={` ${isExportDisabled ? '!cursor-not-allowed !opacity-75' : 'hover:!bg-gray-50'} flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md `}
                            label="PDF"
                            icon={<FiDownload className="text-gray-600" />}
                            iconPosition='left'
                            disabled={isExportDisabled}
                        />
                        <div
                            className={`flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md ${isExportDisabled ? 'cursor-not-allowed opacity-75' : 'hover:bg-gray-50'}`}
                        >
                            <CSVLink
                                data={csvData}
                                onClick={(event) => {
                                    if (isExportDisabled) {
                                        event.preventDefault();
                                    }
                                }}
                                filename="vendors_list.csv"
                                className={`${isExportDisabled ? 'cursor-not-allowed' : ''} flex items-center gap-1`}
                            >
                                <FiDownload className="text-gray-600" />
                                CSV
                            </CSVLink>
                        </div>
                    </div>
                </div>

                <VendorsTable
                    data={vendorData}
                    setData={setVendorData}
                    globalFilter={searchTerm}
                    onGlobalFilterChange={setSearchTerm}
                    newlyAddedItem={newlyAddedItem}
                    setNewlyAddedItem={setNewlyAddedItem}
                    onClearNewlyAddedItem={handleClearNewlyAddedItem}
                    fetchVendors={fetchVendors}
                    loadingData={loading}
                    accountId={accountId}
                    statusOptions={statusOptions}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                />

                <Modal
                    isOpen={openAddModal}
                    onClose={handleCloseAddModal}
                    modalClass={'xl:w-[40%] lg:w-[45%] md:w-[65%] sm:w-[70%] w-[90%] !px-0'}
                >
                    <AddModal
                        handleCloseAddModal={handleCloseAddModal}
                        onAdd={handleAddItem}
                        openEditModal={handleOpenEditModal}
                        accountId={accountId}
                    />
                </Modal>
            </div>
        </div>
    );
};

export default Vendors;